import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Checkbox, Collapse, Form, Grid, Input, InputNumber } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { IconDelete, IconPlus } from '@arco-design/web-react/icon';

import { validateIP } from '@/utils/ip';

import { BindingProviderStep } from './bindingProvider';
import styles from './bindingResource.module.less';

/** 常规数值类配额上限。 */
const MAX_SAFE_QUOTA = Number.MAX_SAFE_INTEGER;
/** Token 限速类配额上限。 */
const MAX_RATE_TOKEN_QUOTA = 10_000_000_000;

/** 资源配置步骤组件属性。 */
interface BindingResourceStepProps {
  /** 当前表单实例。 */
  form: FormInstance;
  /** 通用必填校验规则。 */
  defaultRules?: any[];
  /** 资源限制开关与回调配置。 */
  options?: {
    /** 编辑态回填数据，用于策略展示名兜底。 */
    initData?: Record<string, any> | null;
    /** 是否展示模型供应商选择器，默认展示以兼容订阅创建页。 */
    showProviderSelector?: boolean;
    /** 是否使用适合弹窗场景的折叠紧凑布局。 */
    compact?: boolean;
    /** 策略选择只读（下单固定规格等） */
    strategyReadOnly?: boolean;
    /** 后付费场景隐藏 Token 总量输入 */
    hideBillingTotalTokens?: boolean;
    /**
     * 隐藏「Token 限制」开关卡片（输入/输出 Token 已迁至 model-subscription-provider）。
     * 为 true 时仅按需展示 Token 总量，不再展示开关与输入/输出字段。
     */
    hideTokenLimitCard?: boolean;
    /** Token 总量只读 */
    billingTotalTokensReadOnly?: boolean;
    /** 输入/输出 Token 额度只读 */
    inputTokensReadOnly?: boolean;
    outputTokensReadOnly?: boolean;
    /** 请求数只读 */
    requestsReadOnly?: boolean;
    /** 输入/输出限速只读 */
    inputRateReadOnly?: boolean;
    outputRateReadOnly?: boolean;
    /** 限额开关只读（禁止切换卡片勾选态） */
    limitTogglesReadOnly?: boolean;
    /** 各限额开关独立只读 */
    tokenLimitToggleReadOnly?: boolean;
    requestsToggleReadOnly?: boolean;
    inputRateToggleReadOnly?: boolean;
    outputRateToggleReadOnly?: boolean;
    /** 请求数限制卡片整体隐藏（如下单无数据） */
    hideRequestsCard?: boolean;
    /** 输入 Token 限速卡片整体隐藏 */
    hideInputRateCard?: boolean;
    /** 输出 Token 限速卡片整体隐藏 */
    hideOutputRateCard?: boolean;
    /** 是否启用 Token 限制。 */
    showTokenLimit?: boolean;
    /** 是否启用请求数限制。 */
    showRequests?: boolean;
    /** 是否启用输入 Token 限速。 */
    showInputTokens?: boolean;
    /** 是否启用输出 Token 限速。 */
    showOutputTokens?: boolean;
    /** Token 限制开关回调。 */
    onToggleTokenLimit?: (checked: boolean) => void;
    /** 请求数限制开关回调。 */
    onToggleRequests?: (checked: boolean) => void;
    /** 输入 Token 限速开关回调。 */
    onToggleInputTokens?: (checked: boolean) => void;
    /** 输出 Token 限速开关回调。 */
    onToggleOutputTokens?: (checked: boolean) => void;
    /**
     * 下单：SKU 预置白名单值集合；命中则禁编辑/删除，仍可新增。
     * key 为表单字段名（allowedClientCIDRs / allowedClientIPs）。
     */
    stringListPresetLocks?: Partial<Record<'allowedClientCIDRs' | 'allowedClientIPs', Set<string>>>;
  };
}

/** IP 列表单行布局样式。 */
const IP_LIST_ROW_STYLE = { display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 } as const;

/** 单个资源卡片的展示配置。 */
interface ResourceToggleCardConfig {
  /** 卡片唯一标识。 */
  key: 'tokenLimit' | 'requests' | 'inputTokens' | 'outputTokens';
  /** 卡片标题。 */
  title: string;
  /** 当前是否已启用。 */
  checked: boolean;
  /** 开关切换后需要清空的字段。 */
  fields: string[];
  /** 父组件维护的勾选回调。 */
  onToggle?: (checked: boolean) => void;
}

/** 数字千分位格式化器。 */
const numberFormatter = (value: number | string | undefined) => `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
/** 数字千分位反解析器。 */
const numberParser = (value: string | undefined) => String(value || '').replace(/,/g, '');

/** 资源配置表单片段：资源配额和速率限制。 */
export function BindingResourceStep({ form, defaultRules, options }: BindingResourceStepProps) {
  const { t } = useTranslation();
  const cidrsWatch = Form.useWatch('allowedClientCIDRs', form);
  const ipsWatch = Form.useWatch('allowedClientIPs', form);
  const cidrPresetLocks = options?.stringListPresetLocks?.allowedClientCIDRs;
  const ipPresetLocks = options?.stringListPresetLocks?.allowedClientIPs;

  /** CIDR 白名单校验规则。 */
  const cidrRules = [
    ...(defaultRules || []),
    {
      validator(value: string | undefined, callback: (message?: string) => void) {
        callback(validateIP(String(value || '').trim()) === 'CIDR' ? undefined : t('data-center:model.subscription.allowed.cidr.invalid'));
      },
    },
  ];
  /** 单个 IP 白名单校验规则。 */
  const ipRules = [
    {
      validator(value: string | undefined, callback: (message?: string) => void) {
        const normalized = String(value || '').trim();
        if (!normalized) return callback();
        callback(validateIP(normalized) === 'IPv4' ? undefined : t('data-center:model.subscription.allowed.ip.invalid'));
      },
    },
  ];

  /** 切换资源卡片启用态，并在关闭时清空对应字段。 */
  const handleToggle = (checked: boolean, fields: string[], callback?: (checked: boolean) => void) => {
    callback?.(checked);
    if (!checked) {
      form.setFieldsValue(
        fields.reduce<Record<string, undefined>>((acc, field) => {
          acc[field] = undefined;
          return acc;
        }, {}),
      );
    }
  };

  /** 渲染单个数值输入框，供各资源卡片复用。 */
  const renderQuotaInput = (
    field: string,
    label: string,
    max = MAX_SAFE_QUOTA,
    showLabel = true,
    rules = defaultRules,
    validateTrigger?: string[],
    disabled = false,
  ) => (
    <Form.Item field={field} label={showLabel ? label : undefined} rules={rules} validateTrigger={validateTrigger}>
      <InputNumber
        min={1}
        max={max}
        precision={0}
        step={1000}
        style={{ width: '100%' }}
        disabled={disabled}
        placeholder={t('common:input.placeholder.name', { name: label })}
        formatter={numberFormatter}
        parser={numberParser}
      />
    </Form.Item>
  );

  /** 创建 Token 子额度校验规则：可选填写，但不得超过 Token 总量。 */
  const createTokenSubLimitRules = (label: string) => [
    {
      validator(value: number | undefined, callback: (message?: string) => void) {
        if (value == null) return callback();
        /** 当前填写的输入或输出 Token 额度。 */
        const currentValue = Number(value);
        /** Token 总量字段当前值。 */
        const totalValue = Number(form.getFieldValue('billingTotalTokens'));
        if (Number.isFinite(totalValue) && currentValue > totalValue) {
          return callback(t('data-center:model.subscription.token.sub.limit.exceeds', { name: label }));
        }
        return callback();
      },
    },
  ];

  /** 渲染时间窗口类配额配置块。 */
  const renderWindowLimit = (enabled: boolean, prefix: 'inputTokens' | 'outputTokens', disabled = false) => {
    if (!enabled) return null;
    /** 当前配额名称。 */
    const label =
      prefix === 'inputTokens' ? t('data-center:model.subscription.input.quota') : t('data-center:model.subscription.output.quota');
    return (
      <Grid.Row gutter={16}>
        <Grid.Col span={12}>
          {renderQuotaInput(
            `${prefix}Value`,
            t('data-center:model.subscription.window.token.value', { name: label }),
            MAX_RATE_TOKEN_QUOTA,
            true,
            defaultRules,
            undefined,
            disabled,
          )}
        </Grid.Col>
        <Grid.Col span={12}>
          {renderQuotaInput(
            `${prefix}WindowSeconds`,
            t('data-center:model.subscription.window.seconds'),
            MAX_SAFE_QUOTA,
            true,
            defaultRules,
            undefined,
            disabled,
          )}
        </Grid.Col>
      </Grid.Row>
    );
  };

  /** 渲染卡片头部。 */
  const renderCardHeader = (config: ResourceToggleCardConfig, toggleReadOnly = false) => (
    <label className={styles.cardHeader}>
      <Checkbox
        checked={config.checked}
        disabled={toggleReadOnly}
        onChange={(checked) => handleToggle(checked, config.fields, config.onToggle)}
      />
      <div className={styles.cardHeaderContent}>
        <div className={styles.cardTitle}>{config.title}</div>
      </div>
    </label>
  );

  /** 渲染单个资源勾选卡片。 */
  const renderResourceCard = (config: ResourceToggleCardConfig, content: ReactNode, showBody = true, toggleReadOnly = false) => (
    <div className={`${styles.resourceCard} ${config.checked ? styles.resourceCardChecked : ''}`} key={config.key}>
      {renderCardHeader(config, toggleReadOnly)}
      {config.checked ? showBody ? <div className={styles.cardBody}>{content}</div> : content : null}
    </div>
  );

  /** 渲染字符串列表字段，供 CIDR/IP 两类白名单复用。 */
  const renderStringList = (
    field: 'allowedClientCIDRs' | 'allowedClientIPs',
    placeholder: string,
    rules: any[],
    addLabel: string,
    listWatch: unknown,
    presetLocks?: Set<string>,
  ) => (
    <Form.List field={field}>
      {(fields, { add, remove }) => (
        <>
          {fields.map((item, index) => {
            const list = (Array.isArray(listWatch) ? listWatch : form.getFieldValue(field)) as unknown;
            const raw = Array.isArray(list) ? list[index] : undefined;
            const value = String(raw ?? '').trim();
            const inSkuPreset = Boolean(value && presetLocks?.has(value));
            return (
              <div key={item.key} style={IP_LIST_ROW_STYLE}>
                <Form.Item
                  field={item.field}
                  rules={inSkuPreset ? undefined : rules}
                  style={{ flex: 1, marginBottom: 0 }}
                  validateTrigger={['onSubmit', 'onBlur']}
                >
                  <Input placeholder={placeholder} disabled={inSkuPreset} readOnly={inSkuPreset} />
                </Form.Item>
                {inSkuPreset ? null : (
                  <Button
                    type="text"
                    status="danger"
                    disabled={fields.length <= 1}
                    onClick={() => remove(index)}
                    style={{ width: 40, flexShrink: 0 }}
                  >
                    <IconDelete />
                  </Button>
                )}
              </div>
            );
          })}
          <Button type="dashed" long icon={<IconPlus />} className={styles.addButton} onClick={() => add('')}>
            {addLabel}
          </Button>
        </>
      )}
    </Form.List>
  );

  const hideTokenLimitCard = Boolean(options?.hideTokenLimitCard);

  /** 资源卡片配置集合（Token 限制卡片可由 hideTokenLimitCard 隐藏）。 */
  const cardConfigs: ResourceToggleCardConfig[] = [
    {
      key: 'tokenLimit',
      title: t('data-center:model.subscription.token.limit.label'),
      checked: Boolean(options?.showTokenLimit),
      fields: ['billingTotalTokens', 'inputTokens', 'outputTokens'],
      onToggle: options?.onToggleTokenLimit,
    },
    {
      key: 'requests',
      title: t('data-center:model.subscription.requests.limit'),
      checked: Boolean(options?.showRequests),
      fields: ['requests'],
      onToggle: options?.onToggleRequests,
    },
    {
      key: 'inputTokens',
      title: t('data-center:model.subscription.input.limit'),
      checked: Boolean(options?.showInputTokens),
      fields: ['inputTokensWindowSeconds', 'inputTokensValue'],
      onToggle: options?.onToggleInputTokens,
    },
    {
      key: 'outputTokens',
      title: t('data-center:model.subscription.output.limit'),
      checked: Boolean(options?.showOutputTokens),
      fields: ['outputTokensWindowSeconds', 'outputTokensValue'],
      onToggle: options?.onToggleOutputTokens,
    },
  ];

  const provider =
    options?.showProviderSelector === false ? null : (
      <BindingProviderStep form={form} defaultRules={defaultRules} initData={options?.initData} readOnly={options?.strategyReadOnly} />
    );
  const accessControl = (
    <>
      <Form.Item label={t('data-center:model.subscription.allowed.client.cidrs')}>
        {renderStringList(
          'allowedClientCIDRs',
          t('data-center:model.subscription.allowed.client.cidrs.placeholder'),
          cidrRules,
          t('common:add'),
          cidrsWatch,
          cidrPresetLocks,
        )}
      </Form.Item>
      <Form.Item label={t('data-center:model.subscription.allowed.client.ips')}>
        {renderStringList(
          'allowedClientIPs',
          t('data-center:model.subscription.allowed.client.ips.placeholder'),
          ipRules,
          t('common:add'),
          ipsWatch,
          ipPresetLocks,
        )}
      </Form.Item>
    </>
  );
  const resourceLimits = (
    <div className={`${styles.resourceCardList} ${options?.compact ? styles.compactResourceCardList : ''}`}>
      {hideTokenLimitCard
        ? options?.hideBillingTotalTokens
          ? null
          : renderQuotaInput(
              'billingTotalTokens',
              t('data-center:model.subscription.token.total.label'),
              MAX_SAFE_QUOTA,
              true,
              [],
              undefined,
              options?.billingTotalTokensReadOnly,
            )
        : renderResourceCard(
            cardConfigs[0],
            <div className={styles.tokenLimitBlock}>
              {options?.hideBillingTotalTokens
                ? null
                : renderQuotaInput(
                    'billingTotalTokens',
                    t('data-center:model.subscription.token.total.label'),
                    MAX_SAFE_QUOTA,
                    true,
                    [],
                    undefined,
                    options?.billingTotalTokensReadOnly,
                  )}
              {renderQuotaInput(
                'inputTokens',
                t('data-center:model.subscription.token.input'),
                MAX_SAFE_QUOTA,
                true,
                createTokenSubLimitRules(t('data-center:model.subscription.token.input')),
                ['onBlur'],
                options?.inputTokensReadOnly,
              )}
              {renderQuotaInput(
                'outputTokens',
                t('data-center:model.subscription.token.output'),
                MAX_SAFE_QUOTA,
                true,
                createTokenSubLimitRules(t('data-center:model.subscription.token.output')),
                ['onBlur'],
                options?.outputTokensReadOnly,
              )}
            </div>,
            true,
            options?.limitTogglesReadOnly || options?.tokenLimitToggleReadOnly,
          )}
      {options?.hideRequestsCard
        ? null
        : renderResourceCard(
            cardConfigs[1],
            <div className={styles.singleFieldBlock}>
              {renderQuotaInput(
                'requests',
                t('data-center:model.subscription.requests.total'),
                MAX_SAFE_QUOTA,
                false,
                defaultRules,
                undefined,
                options?.requestsReadOnly,
              )}
            </div>,
            false,
            options?.limitTogglesReadOnly || options?.requestsToggleReadOnly,
          )}
      {options?.hideInputRateCard
        ? null
        : renderResourceCard(
            cardConfigs[2],
            <div className={styles.windowLimitBlock}>
              {renderWindowLimit(cardConfigs[2].checked, 'inputTokens', options?.inputRateReadOnly)}
            </div>,
            true,
            options?.limitTogglesReadOnly || options?.inputRateToggleReadOnly,
          )}
      {options?.hideOutputRateCard
        ? null
        : renderResourceCard(
            cardConfigs[3],
            <div className={styles.windowLimitBlock}>
              {renderWindowLimit(cardConfigs[3].checked, 'outputTokens', options?.outputRateReadOnly)}
            </div>,
            true,
            options?.limitTogglesReadOnly || options?.outputRateToggleReadOnly,
          )}
    </div>
  );

  if (options?.compact) {
    const hasEnabledLimit = cardConfigs.some((item) => item.checked);
    return (
      <>
        {provider}
        <Collapse defaultActiveKey={hasEnabledLimit ? ['resourceLimits'] : []} className={styles.compactCollapse}>
          <Collapse.Item header={t('components:access.control')} name="accessControl">
            {accessControl}
          </Collapse.Item>
          <Collapse.Item header={t('common:resource.configuration')} name="resourceLimits">
            {resourceLimits}
          </Collapse.Item>
        </Collapse>
      </>
    );
  }

  return (
    <div className={styles.resourceCardList}>
      {provider}
      {accessControl}
      {resourceLimits}
    </div>
  );
}
